import { Component, h, Prop, State, Host, Watch, Element } from "@stencil/core";
import { getSvgContent, iconContent } from "./request";
import { getAssetsPath } from "./getAssetsPath";
import { iconNames as webIconNames } from "../../themes/web/assets/icons/icons-list/iconNames";
import { iconNames as responsiveIconNames } from "../../themes/responsive/assets/icons/icons-list/iconNames";

import { IconNames } from "./types";

/**
 * @slot (default) - not to be used, it's purpose is to prevent icon duplication when
 *                   the web component is cloned.
 */

/**
 * this component is heavily inspired by https://github.com/ionic-team/ionicons
 */
@Component({
    tag: "ux-icon",
    styleUrl: "icon.scss",
    shadow: false,
})
export class Icon {
    @Element() iconElement: HTMLUxIconElement;
    /** name of icon */
    @Prop({ reflect: true }) name: string = "";
    /** contains the full svg */
    @State() private svgContent: string = null;
    /** path to assets directory */
    @State() private assetsPath: string = "";

    /** array with the supported values for the fill prop, can be expanded if needed */
    private supportedFills: string[] = ["primary", "white", "black"];
    /** property to change the color of the icon, supported colors: green, white, black */
    @Prop() fill: string = "";

    /** the size of the icon on mobile viewports */
    @Prop() sizeS: number | null = null;
    /** the size of the icon on tablet viewports */
    @Prop() sizeM: number | null = null;
    /** the size of the icon on desktop viewports */
    @Prop() sizeL: number | null = null;
    /** weight of the icon, either "light", "normal", or "bold", defaults to normal */
    @Prop() weight: string = null;
    /** if set, the filled version (if available) will be selected */
    @Prop() filled: boolean = false;

    private hasCustomDimensions =
        this.sizeS !== null || this.sizeM !== null || this.sizeL !== null;

    private getIconDimension = () => {
        if (this.desktopMediaWatcher.matches)
            this.iconDimension = this.sizeL || this.sizeM || this.sizeS;
        else if (this.tabletMediaWatcher.matches)
            this.iconDimension = this.sizeM || this.sizeS || this.sizeL;
        else this.iconDimension = this.sizeS || this.sizeM || this.sizeL;
    };

    private tabletMediaWatcher = window.matchMedia("(min-width: 768px)");
    private desktopMediaWatcher = window.matchMedia("(min-width: 1024px)");

    @State() iconDimension: number;

    connectedCallback() {
        this.assetsPath = getAssetsPath();
        document.addEventListener("uxThemeChange", () =>
            window.setTimeout(() => (this.assetsPath = getAssetsPath()))
        );
        this.tabletMediaWatcher.addEventListener(
            "change",
            this.getIconDimension
        );
        this.desktopMediaWatcher.addEventListener(
            "change",
            this.getIconDimension
        );
    }

    disconnectedCallback() {
        this.tabletMediaWatcher.removeEventListener(
            "change",
            this.getIconDimension
        );
        this.desktopMediaWatcher.removeEventListener(
            "change",
            this.getIconDimension
        );
    }

    componentDidLoad() {
        this.loadIcon();
        if (this.hasCustomDimensions) {
            this.getIconDimension();
        }
    }

    private getIconWeight = (
        weight: string,
        availableWeights: string[]
    ): string => {
        if (availableWeights.includes(weight)) return weight;
        else if (availableWeights.includes("normal")) return "normal";
        else if (availableWeights.includes("light")) return "light";
        else if (availableWeights.includes("bold")) return "bold";
    };

    private getIconFileName = (): string | null => {
        let fileName: string | null = null;
        let iconsList: IconNames[] =
            this.assetsPath.indexOf("/themes/responsive/") !== -1
                ? responsiveIconNames
                : webIconNames;
        let icon = iconsList.find((icon) => icon.name === this.name);
        if (icon !== undefined) {
            const isFilled =
                (this.filled || icon.outlineWeights.length === 0) &&
                icon.filledWeights.length > 0;
            const weights = isFilled ? icon.filledWeights : icon.outlineWeights;
            const weight = this.getIconWeight(this.weight, weights);
            if (!isFilled) fileName = this.name + "--" + weight;
            else fileName = this.name + "--filled--" + weight;
        } else {
            const icon = iconsList.find(
                (icon) => icon.legacyNames[this.name] !== undefined
            );
            if (icon === undefined) return fileName;
            const iconName = icon.name;
            const isFilled = icon.legacyNames[this.name].filled;
            const weights = isFilled ? icon.filledWeights : icon.outlineWeights;
            const iconWeight = icon.legacyNames[this.name].weight;
            const filled = !!icon.legacyNames[this.name].filled;
            const weight = this.getIconWeight(iconWeight, weights);
            if (!filled) fileName = iconName + "--" + weight;
            else fileName = iconName + "--filled--" + weight;
        }
        return fileName;
    };

    @Watch("assetsPath")
    @Watch("name")
    loadIcon() {
        if (this.name === "") {
            return;
        }
        const iconFileName = this.getIconFileName();
        if (iconFileName === null) {
            if (loggedWarnings.indexOf(iconFileName) !== -1) return;
            console.warn(`Icon ${iconFileName} is not available in ux-icon.`);
            loggedWarnings.push(iconFileName);
            return;
        }
        const url = `${this.assetsPath}${iconFileName}.svg`;
        if (iconContent.has(url)) {
            // sync if it's already loaded
            this.svgContent = iconContent.get(url);
        } else {
            // async if it hasn't been loaded
            getSvgContent(url).then(
                () => (this.svgContent = iconContent.get(url))
            );
        }
    }

    render() {
        if (this.name === "") {
            return null;
        }

        const iconClasses = ["ux-icon"];
        if (this.supportedFills.includes(this.fill))
            iconClasses.push(`ux-icon--${this.fill}`);

        return (
            <Host
                class={iconClasses.join(" ")}
                style={
                    this.hasCustomDimensions
                        ? { fontSize: `${this.iconDimension}px` }
                        : null
                }
            >
                <div class="ux-icon__inner" innerHTML={this.svgContent}></div>
                <div class="ux-icon__slot">
                    <slot />
                </div>
            </Host>
        );
    }
}

// this array holds logged warnings about unknown icons to prevent duplicate warnings
const loggedWarnings = [];
